<template>
  <div class="container-fluid">
    <div class="w-full rounded-lg overflow-hidden shadow-lg">
      <Loader :loading="isLoading" />
      <loader-component v-if="isLoading" :loading="isLoading" />
      
      <div class="flex flex-col">
        <!-- Card principal -->
        <div class="w-full bg-white">
          <!-- Tabs Navigation - Mejorado para responsive -->
          <div class="border-b border-gray-200 overflow-x-auto scrollbar-hide">
            <nav class="flex min-w-max md:min-w-0" aria-label="Tabs">
              <button 
                v-for="tab in tabs"
                :key="tab.id"
                @click="activeTab = tab.id" 
                :class="[
                  'flex-shrink-0 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap',
                  activeTab === tab.id 
                    ? 'text-blue-600 border-b-2 border-blue-600' 
                    : 'text-neutral-500 hover:text-gray-700 hover:border-gray-300'
                ]"
              >
                {{ tab.label }}
              </button>
            </nav>
          </div>

          <!-- Tab Content -->
          <div class="p-4 md:p-6">
            <!-- General Tab -->
            <div v-show="activeTab === 'general'" class="space-y-6">
              <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Sección de perfil -->
                <div class="lg:col-span-1">
                  <h3 class="text-xl font-medium text-neutral-500 mb-6 font-['Poppins']">Perfil</h3>
                  
                  <!-- Avatar -->
                  <div class="flex flex-col items-center space-y-4">
                    <div class="relative">
                      <img 
                        :src="imageSrc || defaultProfileImage" 
                        alt="Avatar" 
                        class="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                    </div>

                    <div class="text-center">
                      <a 
                        :href="`${apiBaseUrl}/asesores-inmobiliarios/${slug}`" 
                        class="text-xl font-medium text-blue-600 hover:text-blue-700 hover:underline font-['Poppins']"
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {{ (name || '') + ' ' + (last_name || '') }}
                      </a>
                      
                      <button  
                        @click="reenviar_email(userId)" 
                        :disabled="isPasswordResetDisabled"
                        :class="[
                          'mt-3 w-full text-sm font-medium transition-colors',
                          isPasswordResetDisabled 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-blue-600 hover:text-blue-700 hover:underline'
                        ]"
                      >
                        Recuperar contraseña
                      </button>

                      <!-- Mensajes de alerta -->
                      <div v-if="successMessage" class="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p class="text-xs text-green-800">{{ successMessage }}</p>
                      </div>
                      <div v-if="errorMessage" class="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p class="text-xs text-red-800">{{ errorMessage }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Información Personal -->
                <div class="lg:col-span-2">
                  <h3 class="text-xl font-medium text-neutral-500 mb-6 font-['Poppins']">Información Personal</h3>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Nombre -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Nombre:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ name || '-' }}</p>
                    </div>

                    <!-- Fecha de nacimiento -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Fecha de nacimiento:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ birthday || '-' }}</p>
                    </div>

                    <!-- Apellido paterno -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Apellido paterno:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ last_name || '-' }}</p>
                    </div>

                    <!-- Apellido materno -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Apellido Materno:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ last_second_name || '-' }}</p>
                    </div>

                    <!-- Email -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Email:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ email || '-' }}</p>
                    </div>

                    <!-- Teléfono -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Teléfono:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ phone || '-' }}</p>
                    </div>

                    <!-- Compañía -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Compañía:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">
                        {{ companies.find(company => company.id === user.company_id)?.name || 'Sin compañía asignada' }}
                      </p>
                    </div>

                    <!-- Cargo -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Cargo:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">
                        {{ cargos.find(cargo => cargo.id === user.position_id)?.name || 'Sin cargo asignado' }}
                      </p>
                    </div>

                    <!-- Plan -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Plan:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">
                        {{ plans.find(plan => plan.id === user.plan_id)?.name || 'Sin plan asignado' }}
                      </p>
                    </div>

                    <!-- Tipo de Cuenta -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Tipo de Cuenta:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">
                        {{ accounts.find(account => account.id === user.account_id)?.name || 'Sin cuenta asignado' }}
                      </p>
                    </div>

                    <!-- Creación -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Creación:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ create_account || '-' }}</p>
                    </div>

                    <!-- Culminación de onboarding -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Culminación de onboarding:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ finish_register || '-' }}</p>
                    </div>

                    <!-- Estatus -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Estatus:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ status || '-' }}</p>
                    </div>

                    <!-- Estatus Suscripción -->
                    <div class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500">Estatus Suscripción:</label>
                      <p class="px-3 py-2 bg-gray-50 rounded-lg p-2.5 text-sm text-box">{{ status_sus || '-' }}</p>
                    </div>

                    <!-- Botón Activar Cuenta (si aplica) -->
                    <div v-if="tokenAccess && tokenAccess !== ''" class="space-y-1">
                      <label class="text-sm font-medium text-neutral-500 invisible">Acción:</label>
                      <button 
                        @click="activateAccount"
                        class="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                      >
                        Activar Cuenta
                      </button>
                    </div>
                  </div>

                  <!-- Botones de acción -->
                  <div class="flex flex-col-reverse md:flex-row justify-center gap-3 pt-6 mt-6 border-t border-gray-200">
                    <button 
                      type="button"
                      @click="$router.push('/advisors')"
                      class="px-6 py-2.5 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors min-w-[120px]"
                    >
                      Cancelar
                    </button>
                    <button 
                      @click="editUser(user.id)"
                      class="px-6 py-2.5 bg-tuinColor hover:bg-tuinColor/90 text-white rounded-lg font-medium transition-colors min-w-[160px]"
                    >
                      Editar Perfil
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Otros tabs -->
            <div v-show="activeTab === 'social'">
              <social-networks-form 
                :company-id="companyId" 
                @social-data-updated="handleSocialDataUpdate"
              />
            </div>

            <div v-show="activeTab === 'propiedades'">
              <Properties-list 
                :company-id="companyId" 
                @social-data-updated="handleSocialDataUpdate"
              />
            </div>

            <div v-show="activeTab === 'historial'">
              <ListHistoryComponent
                :property-id="userId" 
                :resp="data_broker"
                Type="agent" 
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Modal -->
      <ResetPassword_modal
        :isVisible="showBlockModal"
        @confirm="reenviar_email(userId)"
        @cancel="closeModal"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import { useStore } from 'vuex';
import { useRoute } from "vue-router";
import 'vue-toastification/dist/index.css';
import Loader from '@/components/loaderComponent.vue';
import defaultProfileImage from '@/assets/imgs/1.png';
import SocialNetworksForm from '@/components/advisor/NetworksAdvisorComponent.vue'; // Import the new component
import PropertiesList from '@/components/advisor/PropertiesAdvisorComponent.vue'; // Import the new component
import ListHistoryComponent from '@/components/advisor/HistoryPaymentAdvisorComponent.vue';
import { useRouter } from "vue-router";

// Obtener la tienda de Vuex
const store = useStore();
const isLoading = ref(true); // Inicializamos isLoading
const activeTab = ref('general'); // Default active tab
const apiBaseUrl = computed(() => process.env.VUE_APP_API_BASE_URL.replace(/\/api$/, ""));
let session = computed(() => store.getters.getUserSession);
const route = useRoute();
let userId = computed(() => route.params.id);
const isPasswordResetDisabled = ref(false);

let selectedRoles = ref([]);
// Variables reactivas
let name = ref(null);
let birthday = ref();
let email = ref();
let aboute_me = ref();
let phone = ref();
let  imageSrc = ref(null);
// Computed que devuelve la URL de la imagen a mostrar
let last_name = ref();
let status = ref();
let slug = ref();
let status_sus = ref();
let last_second_name = ref();
let profile = ref();
let companies = ref([]); // Declaración de companies
let user = ref({ company_id: null });
let roles = ref([]); // Declaración de roles
let cargos = ref([]); // Declaración de cargos
let plans = ref([]); // Declaración de planes
let accounts = ref([]); // Declaración de cuentas
let router = useRouter();
let tokenAccess = ref(""); // Nueva variable para el token
let create_account = ref();
let finish_register = ref();
let data_broker = ref({});

const successMessage = ref('');
const errorMessage = ref('');

const tabs = computed(() => [
  { id: 'general', label: 'General' },
  { id: 'social', label: 'Redes Sociales' },
  { id: 'propiedades', label: 'Propiedades' },
  { id: 'historial', label: 'Historial de Pagos' }
]);

const showToast = (message) => {
  successMessage.value = message;
  setTimeout(() => {
    successMessage.value = ''; // Ocultar el mensaje después de 3 segundos
  }, 3000);
};

const showErrorToast = (message) => {
  errorMessage.value = message;
  setTimeout(() => {
    errorMessage.value = ''; // Ocultar el mensaje después de 3 segundos
  }, 3000);
};

// Función reenviar_email corregida
const reenviar_email = async (userId) => {
  try {
    isPasswordResetDisabled.value = true; // Deshabilitar el botón
    
    const options = {
      headers: {
        'Authorization': `Bearer ${session.value.token_api}`,
        'Content-Type': 'application/json'
      }
    };

    const data = { user_id: userId };

    const response = await axios.post(
      `${process.env.VUE_APP_API_BASE_URL}/broker/profile/resend-activation-email/${userId}`,
      data,
      options
    );

    console.log('Respuesta del servidor:', response.data);

    if (response.status === 200) {
      showToast('Correo reenviado correctamente');
      setTimeout(() => {
        isPasswordResetDisabled.value = false;
      }, 5000); 
    } else {
      showErrorToast('Hubo un error al reenviar.');
      isPasswordResetDisabled.value = false; // Rehabilitar el botón si hay error
    }
  } catch (error) {
    console.error('Error al reenviar correo:', error);
    showErrorToast('Hubo un error inesperado.');
    isPasswordResetDisabled.value = false; // Rehabilitar el botón si hay error
  }
};

const handleSocialDataUpdate = (socialData) => {
      // You can add logic here to handle social data if needed
      console.log('Social data updated:', socialData);
  };
console.log(imageSrc)
// Obtener perfil
const translateStatus = (status) => {
  const statusTranslations = {
    active: 'Activo',
    delete: 'Desactivado',
    block: 'Bloqueado',
    pending: 'Pendiente',
    expired: 'Expirada',
    // Agrega más traducciones según sea necesario
  };

  return statusTranslations[status] || status; // Devuelve el estado original si no se encuentra traducción
};
function formatDate(isoDate) {
    const date = new Date(isoDate);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

async function getProfile() {
  try {
    let options = {
      headers: {
        'Authorization': `Bearer ${session.value.token_api}`
      }
    };

    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/broker/profile/${userId.value}`, options
    );
    console.log("prueba:", response)
    if (response.status === 401) {
      store.dispatch('clearUserSession');
    }

    if (response.status === 200) {
      data_broker.value = response;
      profile.value = response.data.resp.info;
      name.value = response.data.resp.info.name;
      last_name.value = response.data.resp.info.last_name;
      last_second_name.value = response.data.resp.info.last_second_name;
      email.value = response.data.resp.info.email;
      phone.value = response.data.resp.info.phone;
      slug.value = response.data.resp.info.slug;
      create_account.value = formatDate(response.data.resp.info.created_at);
      finish_register.value = response.data.resp.info.finish_register;
      status.value = translateStatus(response.data.resp.info.status);
      const info = response.data.resp.info;

      const subscriptionStatus =
        info.last_subscription?.status ?? 'sin_suscripcion';

      status_sus.value = translateStatus(subscriptionStatus);
      aboute_me.value = response.data.resp.info.about_me;
      birthday.value = response.data.resp.info.birthday;
      user.value.company_id = response.data.resp.info.company_id; // Asigna el company_id
      user.value.plan_id = response.data.resp.info.plan_id; // Asigna el plan_id
      user.value.position_id = response.data.resp.info.position_id; // Asigna el position_id
      const profileAvatar = response.data.resp.info.profile_avatar;

      // Asigna la imagen de avatar
      if (profileAvatar) {
        imageSrc.value = `${profileAvatar}`;
      } else {
        imageSrc.value = null; // O un valor por defecto
      }
      console.log(imageSrc.value);
      console.log(response.data)
      tokenAccess.value = response.data.resp.info.token_access; // Guardar el token
      console.log("Token access:", tokenAccess.value); // Verificar el token
      // Extrae los IDs de los roles asociados y asígnalos a selectedRoles
      selectedRoles.value = response.data.resp.info.roles.map(role => role.id);
    }
    isLoading.value = false; 
  } catch (error) {
    console.error("Error fetching data:", error);
  }
}
async function editUser (id) {
  console.log("ID del usuario:", id); // Imprime el ID en la consola
  router.push({ name: 'AdvisorForm', params: { id } }); // Redirige a la ruta 'UserForm' con el ID del usuario
}
//async function updateProfileImage(event) {
//    const file = event.target.files[0];
//    if (file) {
//      // Similar al de portada, puedes cargar la imagen en tu servidor o mostrarla localmente
//      const reader = new FileReader();
//      reader.onload = () => {
//        imageSrc.value = reader.result;   // Actualiza la imagen en la vista
//        console.log(imageSrc)
//      };
//      reader.readAsDataURL(file);  // Convierte la imagen a base64
//    }
//  }
// Obtener compañías
async function fetchCompanies() {
  try {
    let options = {
      headers: {
        'Authorization': `Bearer ${session.value.token_api}`
      }
    };
    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/companies`, options
    );

    if (response.status === 401) {
      store.dispatch('clearUserSession');
    }

    if (response.status === 200 && response.data?.resp?.companies) {
      companies.value = response.data.resp.companies; // Cambia esto
    } else {
      console.error("Error en la respuesta de la API: estructura inesperada");
    }
  } catch (error) {
    console.error("Error al obtener las compañías:", error);
  }
}

// Función para obtener roles
async function fetchRoles() { 
  try {
    let options = {
      headers: {
        'Authorization': `Bearer ${session.value.token_api}`
      }
    };
    const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/roles`, options);
    
    // Verifica si la respuesta es un error de autorización
    if (response.status === 401) {
      store.dispatch('clearUserSession');
    }

    if (response.status === 200) {
      roles.value = response.data; // Asigna los roles correctamente
    }
  } catch (error) {
    console.error("Error al obtener los roles:", error);
  }
}

async function activateAccount() {
  if (!tokenAccess.value) {
    alert("No se encontró un token válido. Asegúrate de cargar el perfil primero.");
    return;
  }

  isLoading.value = true;

  try {
    const response = await axios.post(
      `${process.env.VUE_APP_API_BASE_URL}/verify-account`,
      { token: tokenAccess.value }, // Enviar el token
      { headers: { 'Authorization': `Bearer ${session.value.token_api}` } }
    );

    if (response.status === 200 && response.data.status) {
      console.log("Cuenta activada con éxito:", response.data.message);
      alert("Cuenta activada correctamente");
      router.push({ name: "Dashboard" }); // Redirige al dashboard o a otra vista
    }
  } catch (error) {
    console.error("Error al activar la cuenta:", error);
    if (error.response) {
      alert(error.response.data.message || "Error al activar la cuenta");
    }
  } finally {
    isLoading.value = false;
  }
}
//obtener los cargos
async function fetchCargos() {
  try {
    let options = {
      headers: {
        'Authorization': `Bearer ${session.value.token_api}`
      }
    };
    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/cargos`, options
    );

    if (response.status === 401) {
      store.dispatch('clearUserSession');
    }

    // Accede a los cargos en response.data.cargos
    if (response.status === 200 && response.data?.cargos) {
      cargos.value = response.data.cargos; 
    } else {
      console.error("Error en la respuesta de la API: estructura inesperada");
    }
  } catch (error) {
    console.error("Error al obtener los cargos:", error);
  }
}

//obtener los cargos
async function fetchPlanes() {
  try {
    let options = {
      headers: {
        'Authorization': `Bearer ${session.value.token_api}`
      }
    };
    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/planes`, options
    );

    if (response.status === 401) {
      store.dispatch('clearUserSession');
    }

    // Accede a los cargos en response.data.cargos
    if (response.status === 200 && response.data?.plans) {
      plans.value = response.data.plans; 
    } else {
      console.error("Error en la respuesta de la API: estructura inesperada");
    }
  } catch (error) {
    console.error("Error al obtener los planes:", error);
  }
}

//obtener los cargos
async function fetchAccounts() {
  try {
    let options = {
      headers: {
        'Authorization': `Bearer ${session.value.token_api}`
      }
    };
    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/account`, options
    );

    if (response.status === 401) {
      store.dispatch('clearUserSession');
    }

    // Accede a los cargos en response.data.cargos
    if (response.status === 200 && response.data?.accounts) {
      accounts.value = response.data.accounts; 
    } else {
      console.error("Error en la respuesta de la API: estructura inesperada");
    }
  } catch (error) {
    console.error("Error al obtener los planes:", error);
  }
}

// Llama a la función para obtener los roles al montar el componente
onMounted(async () => {
  await fetchCompanies();
  await fetchRoles();
  await fetchCargos();
  await fetchPlanes();
  await fetchAccounts();
  await getProfile();  // Asegúrate de obtener el perfil después de las compañías
});
</script>


<style scoped>
  /* Ocultar scrollbar en tabs pero mantener funcionalidad */
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  /* Asegurar que la fuente Poppins se cargue */
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600&display=swap');
</style>